import React, { FC, useEffect } from 'react'
import shallow from 'zustand/shallow'
import { useTranslation } from 'react-i18next'
import { Divider, Input } from 'antd'
import { Control, Controller, useFieldArray, useForm, useWatch } from 'react-hook-form'
import { ErrorMessage } from '@hookform/error-message'
import PerText from '../Text'
import PerFlexRow from '../FlexRow'
import PerButtonCircle from '../ButtonCircle'
import { useAuth } from '../../../context/auth-context'
import { useDateTime } from '../../../shared/useDateTime'
import { TargetTemplateModalComment } from './TargetTemplateModalComment'
import { MaxCharactersWrapper } from '../../formik-component/MaxCharactersWrapper'
import { useTargetTemplateModalStore } from './TargetTemplateModalStore'
import { TargetTemplateModalCommentInput } from './TargetTemplateModalCommentInput'
import { TargetTemplateModalFormErrors } from './types'
import './index.less'
import { ObjectiveKeyResultDto, ObjectiveTemplateDto } from '../../../types'

const MAX_CHARS = 4000

const { TextArea } = Input

const getTasksLength = (tasks: Array<ObjectiveKeyResultDto>) => tasks.map((task) => task.text).join('').length

const TaskCharacterCounter = ({ control }: { control: Control<ObjectiveTemplateDto> }) => {
  const { t } = useTranslation()
  const tasks = useWatch({ control, name: 'objectiveKeyResults' })
  return !!tasks && !!tasks.length ? (
    <PerText fontSize="xs" color="secondary" align="right" mt="xxxs">
      {`${t('targetTemplateModal.totalOfCharInTasks')} ${getTasksLength(tasks)} / ${MAX_CHARS}`}
    </PerText>
  ) : null
}

export const TargetTemplateModalContent: FC = () => {
  const { t } = useTranslation()
  const { user } = useAuth()
  const { utcDateStringToMoment } = useDateTime(user)

  const [currentObjective, currentComments, setNewTargetTemplateForm] = useTargetTemplateModalStore(
    (state) => [state.currentObjective, state.currentComments, state.setNewTargetTemplateForm],
    shallow,
  )

  const newTargetTemplateForm = useForm<ObjectiveTemplateDto>({
    defaultValues: !!currentObjective
      ? { ...currentObjective }
      : {
          objectiveKeyResults: [{ text: '', displayOrder: 1 }],
        },
    criteriaMode: 'all',
    mode: 'onChange',
  })

  const {
    control,
    formState: { errors },
    getValues,
    trigger,
  } = newTargetTemplateForm

  const { fields, remove, append } = useFieldArray({
    name: 'objectiveKeyResults',
    keyName: 'id',
    control,
  })

  const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    const hasValidSelection = e.currentTarget.selectionStart !== e.currentTarget.selectionEnd
    if (getTasksLength(getValues().objectiveKeyResults) >= MAX_CHARS) {
      if (!hasValidSelection) {
        e.preventDefault()
      }
    }
  }

  const handleOnPaste = (e: React.ClipboardEvent) => {
    const currentChars = getTasksLength(getValues().objectiveKeyResults)
    if (currentChars + e.clipboardData.getData('text/plain').length >= MAX_CHARS) {
      e.preventDefault()
    }
  }

  const hasError = (errors: TargetTemplateModalFormErrors, fieldName: string, index: number) => {
    if (index > -1) {
      return (
        !!errors && !!errors.objectiveKeyResults && !!errors.objectiveKeyResults[index] && !!errors.objectiveKeyResults[index][fieldName]
      )
    }
    return !!errors && !!errors[fieldName]
  }

  useEffect(() => {
    setNewTargetTemplateForm(newTargetTemplateForm)
    trigger()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <>
      <PerFlexRow direction="column" mt="m">
        <PerText appearance="label-small" mb="xxs">
          {t('targetTemplateModal.title')}
        </PerText>
        <Controller
          name="objectiveTitle"
          control={control}
          rules={{
            required: `${t('formError.generalRequired')}`,
            validate: (title) => {
              return !!title && title.trim().length === 0 ? `${t('formError.generalRequired')}` : undefined
            },
          }}
          render={({ field: { onChange, value } }) => (
            <>
              <div
                className={hasError(errors as TargetTemplateModalFormErrors, 'objectiveTitle', -1) ? 'has-error' : ''}
                style={{ width: '100%' }}
              >
                <TextArea
                  value={value || undefined}
                  onChange={onChange}
                  rows={1}
                  maxLength={MAX_CHARS}
                  style={{ width: '100%', lineHeight: 1.6 }}
                  placeholder={t('targetTemplateModal.titlePlaceholder')}
                  data-e2e-target-title
                />
              </div>
              <PerFlexRow align="middle" justify="space-between" className="fullWidth">
                <PerFlexRow justify="start">
                  <ErrorMessage
                    errors={errors}
                    name="objectiveTitle"
                    render={({ messages }) => {
                      return (
                        <PerText fontSize="xs" color="error">
                          {(!!messages && !!messages.required && messages.required) ||
                            (!!messages && !!messages.validate && messages.validate)}
                        </PerText>
                      )
                    }}
                  />
                </PerFlexRow>
                <PerFlexRow justify="end">
                  <MaxCharactersWrapper usedCharacters={!!value ? value.length : 0} maxCharacters={MAX_CHARS} />
                </PerFlexRow>
              </PerFlexRow>
            </>
          )}
        />
      </PerFlexRow>

      <PerFlexRow mt="s">
        <PerText appearance="tile-heading">{t('targetTemplateModal.tasks')}</PerText>
      </PerFlexRow>

      <PerFlexRow direction="column">
        {fields.map((field, index) => {
          return (
            <React.Fragment key={index}>
              <PerFlexRow mt="s" key={field.id} className="fullWidth" align="middle" justify="start" noWrap>
                <Controller
                  name={`objectiveKeyResults.${index}.text`}
                  control={control}
                  rules={{
                    required: `${t('formError.generalRequired')}`,
                    minLength: { value: 2, message: `${t('formError.textLength')}` },
                    validate: (task) => {
                      return !!task && task.trim().length === 0 ? `${t('formError.generalRequired')}` : undefined
                    },
                  }}
                  render={({ field: { onChange, value } }) => (
                    <div
                      className={hasError(errors as TargetTemplateModalFormErrors, 'text', index) ? 'has-error' : ''}
                      style={{ width: '90%' }}
                    >
                      <TextArea
                        value={value || undefined}
                        onChange={onChange}
                        rows={1}
                        style={{ width: '100%', lineHeight: 1.6 }}
                        onKeyPress={handleKeyPress}
                        onPaste={handleOnPaste}
                        placeholder={t('targetTemplateModal.taskPlaceholder')}
                        data-e2e-keyresult-input
                      />
                    </div>
                  )}
                />
                <PerFlexRow align="middle" justify="start">
                  {fields.length > 1 && (
                    <PerButtonCircle
                      icon="remove_stroke"
                      size="small"
                      variant="pastel"
                      color="secondary"
                      ml="xs"
                      onClick={() => remove(index)}
                      data-e2e-keyresult-button="remove"
                    />
                  )}
                  {index === fields.length - 1 && (
                    <PerButtonCircle
                      icon="add_stroke"
                      size="small"
                      variant="pastel"
                      color="success"
                      ml="xxs"
                      disabled={!!errors.objectiveKeyResults}
                      onClick={() => {
                        append({ text: '', displayOrder: index + 2 } as ObjectiveKeyResultDto)
                        trigger()
                      }}
                      data-e2e-keyresult-button="add"
                    />
                  )}
                </PerFlexRow>
              </PerFlexRow>
              <PerFlexRow align="middle" justify="space-between" width="90%">
                <PerFlexRow align="middle" justify="start">
                  <PerText fontSize="xs" color="error" mt="xxxs">
                    <ErrorMessage
                      errors={errors}
                      name={`objectiveKeyResults.${index}.text`}
                      render={({ messages }) => {
                        return (
                          <>
                            <PerText fontSize="xs" color="error">
                              {(!!messages && !!messages.required && messages.required) ||
                                (!!messages && !!messages.validate && messages.validate) ||
                                (!!messages && !!messages.minLength && messages.minLength)}
                            </PerText>
                          </>
                        )
                      }}
                    />
                  </PerText>
                </PerFlexRow>
                <PerFlexRow align="middle" justify="end">
                  {index === fields.length - 1 && <TaskCharacterCounter control={control} />}
                </PerFlexRow>
              </PerFlexRow>
            </React.Fragment>
          )
        })}
      </PerFlexRow>

      <Divider />

      <PerFlexRow>
        <PerText appearance="tile-heading">{t('targetTemplateModal.comments')}</PerText>
      </PerFlexRow>

      {currentComments
        .slice()
        .sort((a, b) => {
          const date1 = utcDateStringToMoment(a.date)
          const date2 = utcDateStringToMoment(b.date)
          return date1 && date2 && date1.isBefore(date2) ? -1 : date1 && date2 && date1.isAfter(date2) ? 1 : 0
        })
        .map((note, index) => {
          return <TargetTemplateModalComment comment={note} index={index} key={`${index}${note.date}`} />
        })}

      <TargetTemplateModalCommentInput />
    </>
  )
}
